import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useToast } from "../../../hooks/toast";
import api from "../../../service";
import { useCallback } from "react";

import {
  FormContainer,
  DefaulterWarning,
  FormText,
  FormTextContainer,
  Horizontal,
} from "./styles";
import { useLoading } from "../../../hooks/loading";
import printJS from "print-js";
import { base64ToArrayBuffer, mergePdfs } from "../../../utils/file";
import generateHeaders from "../../../utils/generateHeaders";
import { Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { ModalButtonsWrapper } from "../../Defaulters/styles";
import Button from "../../Defaulters/components/button";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { FormRow } from "../../../components/ControlledInputs/styles";
import ControlledTextareaInput from "../../../components/ControlledInputs/input-textarea";

const getSchema = (isDefaulter) => {
  const baseSchema = {};

  if (isDefaulter) {
    return z.object({
      ...baseSchema,
      reason: z
        .string()
        .min(1, {
          message: "error.field_required",
        })
        .refine((data) => {
          return data.trim().length > 1;
        }),
    });
  }

  return z.object(baseSchema);
};

const PrintDuplicateModal = ({
  open,
  setOpen,
  credentialId,
  availableCredentials,
  collaboratorName,
  updateCallback,
  defaulter = false,
}) => {
  const hasAvailableCredentials = availableCredentials > 0;
  const [isDefaulter, setIsDefaulter] = useState(defaulter && hasAvailableCredentials);
  const { token } = useSelector((state) => state.auth);
  const intl = useIntl();
  const { addToast } = useToast();
  const { handleLoading } = useLoading();

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(getSchema(defaulter)),
    defaultValues: {},
  });

  const handlePrint = useCallback(
    async (values) => {
      try {
        const options = generateHeaders(token);

        handleLoading(true);

        const response = await api.post(
          "/credential-reports/duplicate-and-print",
          {
            credentialId,
            overrideReason: values.reason || "",
            channel: "ADMIN",
          },
          options
        );

        const { data } = response;
        const filteredOutput = data.reportOutputList.filter((value) => value);

        if (filteredOutput.length <= 0) {
          return;
        }

        let arrayBuffers = filteredOutput.map((apiResult) => {
          return base64ToArrayBuffer(apiResult.file);
        });

        const base64String = btoa(
          [].reduce.call(
            new Uint8Array(await mergePdfs(arrayBuffers)),
            function (p, c) {
              return p + String.fromCharCode(c);
            },
            ""
          )
        );

        updateCallback();

        printJS({
          printable: base64String,
          type: "pdf",
          base64: true,
        });

        setOpen(false);
      } catch (err) {
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: `api.error.${err.response?.data?.code}`,
          }),
        });
      } finally {
        handleLoading(false);
      }
    },
    [
      token,
      handleLoading,
      credentialId,
      updateCallback,
      setOpen,
      addToast,
      intl,
    ]
  );

  const redirectToEcommercePage = useCallback(
    async () => {
      try {
        setOpen(false);

        handleLoading(true);

        const response = await api.post(
          `/admin/ecommerce/auth-by-credential/${credentialId}`,
          {},
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "*/*",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        window.open(
          response.data.ecommerceChlebaAuthenticationOutput.generatedUrl,
          "_blank"
        )
      } catch (err) {
        if (err.response.data.code === -18009
            || err.response.data.code === -18020
            || err.response.data.code === -18022
            || err.response.data.code === -18023) {
          addToast({
            type: "info",
            title: intl.formatMessage({
              id: "info",
            }),
            description: intl.formatMessage({
              id: "api.error." + err.response.data.code,
            }),
          });
        } else {
          addToast({
            type: "error",
            title: intl.formatMessage({
              id: "error",
            }),
            description: intl.formatMessage(
              { id: "api.error." + err.response.data.code },
              { errors: err.response.data.ecommerceChlebaAuthenticationOutput?.errorMessage }
            ),
          });
        }
      } finally {
        handleLoading(false);
      }
    },
    [
      handleLoading,
      token,
      addToast,
      intl,
      credentialId,
    ]
  );

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      centered={true}
      closable={false}
      width={600}
      footer={""}
      className="modalLesson"
      destroyOnClose
    >
      {!isDefaulter && (
        <FormContainer onSubmit={handleSubmit(handlePrint)}>
          <FormTextContainer>
            <FormText>
              {intl.formatMessage({
                id: availableCredentials > 0
                  ? "confirm_duplicate_of_credential"
                  : "refuse_duplicate_of_credential",
              })}
            </FormText>
            <Horizontal>
              <FormText>
                {intl.formatMessage(
                  { id: "available_credentials_with_colon" },
                )}
              </FormText>
              <FormText bold>
                {availableCredentials}
              </FormText>
            </Horizontal>
            <Horizontal>
              <FormText>
                {intl.formatMessage(
                  { id: "collaborator_name_with_colon" },
                )}
              </FormText>
              <FormText bold>
                {collaboratorName}
              </FormText>
            </Horizontal>
          </FormTextContainer>
          {defaulter && hasAvailableCredentials && (
            <FormRow marginBottom={"24px"}>
              <ControlledTextareaInput
                name={"reason"}
                control={control}
                formState={formState}
                label={"provide_reason"}
                required={true}
                min={1}
                max={300}
              />
            </FormRow>
          )}
          <ModalButtonsWrapper width="100%">
            <Button
              type="button"
              variant="secondary"
              label={"cancel"}
              shadow
              onClick={() => setOpen(false)}
            />
            { hasAvailableCredentials ? (
              <Button
                label={"confirm"}
                shadow
              />
            ) : (
              <Button
                type="button"
                label={"purchase"}
                shadow
                onClick={redirectToEcommercePage}
              />
            )}
          </ModalButtonsWrapper>
        </FormContainer>
      )}

      {isDefaulter && (
        <DefaulterWarning>
          <ExclamationCircleOutlined
            style={{
              width: "64px",
              height: "64px",
              color: "#002244",
            }}
          />
          <p>
            <FormattedMessage id="credential_locked" />
          </p>
          <ModalButtonsWrapper width="100%">
            <Button
              variant="secondary"
              label={"close"}
              shadow
              onClick={() => setOpen(false)}
            />
            <Button
              label={"open_exception"}
              shadow
              onClick={() => setIsDefaulter(false)}
            />
          </ModalButtonsWrapper>
        </DefaulterWarning>
      )}
    </Modal>
  );
};

export default PrintDuplicateModal;
